import { FormEvent, ReactNode, useState } from "react";
import { ArrowTopRightOnSquareIcon, MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import Modal from "../../layouts/Modal";
import LoadingWheel from "../../components/shared/LoadingWheel";
import QuoteOwnerPill from "../../components/shared/Quotes/QuoteOwnerPill";
import { useVrmTrace, VrmTraceLead, VrmTraceQuote } from "../../data/useFunctions";

// Enums arrive as numbers. Kept here rather than in models because these mirror the API enums
// exactly (models/Lead's LeadStatus stops before Call List One).
const quoteStatus = ["No match", "Match", "Priced", "Accepted collection", "Accepted drop-off", "Rejected", "Calculated"];
const stepNames = ["", "Confirm", "Engine", "Body", "Contact", "Prices", "Personal", "Payment", "Complete"];
const sendStatus = ["Not sent", "Pending", "Sending", "Sent", "Failed", "Skipped"];
const leadStatus = [
  "Awaiting contact", "Scheduled", "Cancelled", "Reschedule", "Collected", "Awaiting payment",
  "Awaiting COD", "Customer to call back", "Completed", "All", "Call list one",
];
const leadWebsite = ["GPM", "SCC", "CTB", "RMC", "Motorwise", "All", "SellAVehicle", "Widget"];
const routingOutcome = ["Normal", "Diverted to SAV (duplicate reg)", "Flagged (duplicate reg)", "Held by SAV for allocation"];
const messageStatus = ["Waiting", "Accepted", "Queued", "Sending", "Sent", "Failed", "Delivered", "Undelivered", "Received", "Read"];

const formatDate = (value: string | null) =>
  value
    ? new Date(value).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })
    : "—";

const formatPrice = (value: number | null) => (value != null && value > 0 ? `£${value.toLocaleString("en-GB")}` : "—");

const shortId = (id: string) => id.slice(0, 8).toUpperCase();

function Customer({ id }: { id: string | null }) {
  return id ? <QuoteOwnerPill customerId={id} showLabel={false} /> : <span className="text-gray-400">—</span>;
}

function Badge({ tone, children }: { tone: "gray" | "green" | "amber" | "red" | "blue"; children: ReactNode }) {
  const tones = {
    gray: "bg-gray-100 text-gray-700",
    green: "bg-green-50 text-green-700",
    amber: "bg-amber-50 text-amber-800",
    red: "bg-red-50 text-red-700",
    blue: "bg-blue-50 text-blue-700",
  };
  return <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${tones[tone]}`}>{children}</span>;
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-gray-500">{label}</dt>
      <dd className="mt-0.5 text-sm text-gray-900 break-words">{children}</dd>
    </div>
  );
}

function sendTone(status: number): "gray" | "green" | "amber" | "red" {
  if (status === 3) return "green";
  if (status === 4) return "red";
  if (status === 1 || status === 2) return "amber";
  return "gray";
}

function QuoteBlock({ quote, leads }: { quote: VrmTraceQuote; leads: VrmTraceLead[] }) {
  const website = quote.sourceWebsiteDomain?.replace(/^https?:\/\/(www\.)?/, "").replace(/\/$/, "");

  return (
    <div className="rounded-lg border border-gray-200 bg-white">
      <div className="flex flex-wrap items-center gap-2 border-b border-gray-100 px-4 py-3">
        <span className="text-sm font-semibold text-gray-900">Quote {formatDate(quote.createdDate)}</span>
        <Badge tone="blue">{quote.origin}</Badge>
        {website && <Badge tone="gray">{website}</Badge>}
        <Badge tone="gray">
          {quoteStatus[quote.status] ?? quote.status} · step {quote.step} {stepNames[quote.step] ?? ""}
        </Badge>
        <span className="ml-auto">
          {quote.visibleToSav ? (
            <a
              href={`/abandoned-quotes/${quote.id}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-xs font-medium text-primary-600 hover:text-primary-700"
            >
              Open quote <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
            </a>
          ) : (
            <Badge tone="amber">Hidden from SAV · assigned to another customer</Badge>
          )}
        </span>
      </div>

      <dl className="grid grid-cols-1 gap-4 px-4 py-3 sm:grid-cols-2 lg:grid-cols-3">
        <Field label="Contact">
          <div>{quote.contactName?.trim() || "—"}</div>
          <div className="text-xs text-gray-500">{[quote.contactPhone, quote.contactEmail].filter(Boolean).join(" · ")}</div>
        </Field>
        <Field label="Collection price">
          <div className="flex flex-wrap items-center gap-2">
            {formatPrice(quote.priceCollected)} <Customer id={quote.collectedCustomerId} />
          </div>
        </Field>
        <Field label="Drop-off price">
          <div className="flex flex-wrap items-center gap-2">
            {formatPrice(quote.priceDropOff)} <Customer id={quote.dropOffCustomerId} />
          </div>
        </Field>
        <Field label="Source customer">
          <Customer id={quote.sourceCustomerId} />
        </Field>
        <Field label="Assigned to">
          <Customer id={quote.assignedCustomerId} />
        </Field>
        <Field label="Amended price">{formatPrice(quote.amendedPrice)}</Field>
        <Field label="Superchat">
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone={sendTone(quote.superchatStatus)}>{sendStatus[quote.superchatStatus] ?? quote.superchatStatus}</Badge>
            {quote.superchatSentOn && <span className="text-xs text-gray-500">{formatDate(quote.superchatSentOn)}</span>}
          </div>
          {quote.superchatAutomation && <div className="text-xs text-gray-500">Automation: {quote.superchatAutomation}</div>}
        </Field>
        <Field label="Prices email">
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone={sendTone(quote.pricesEmailStatus)}>{sendStatus[quote.pricesEmailStatus] ?? quote.pricesEmailStatus}</Badge>
            {quote.pricesEmailSentOn && <span className="text-xs text-gray-500">{formatDate(quote.pricesEmailSentOn)}</span>}
          </div>
        </Field>
        <Field label="Call centre handoff">{formatDate(quote.handedToCallCentreOn)}</Field>
        <Field label="Converted to lead">{formatDate(quote.convertedToLeadOn)}</Field>
        <Field label="Ad click">{quote.hasAdClick ? "Yes" : "No"}</Field>
        {quote.utmParams && <Field label="UTM">{quote.utmParams}</Field>}
      </dl>

      {leads.length > 0 && (
        <div className="flex flex-col gap-3 border-t border-gray-100 bg-gray-50 px-4 py-3">
          {leads.map((lead) => (
            <LeadBlock key={lead.id} lead={lead} />
          ))}
        </div>
      )}
    </div>
  );
}

function LeadBlock({ lead }: { lead: VrmTraceLead }) {
  return (
    <div className="rounded-md border border-gray-200 bg-white px-4 py-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-semibold text-gray-900">Lead {shortId(lead.id)}</span>
        <span className="text-xs text-gray-500">{formatDate(lead.createdDate)}</span>
        <Badge tone="gray">{leadStatus[lead.status] ?? lead.status}</Badge>
        {lead.deletedDate && <Badge tone="red">Deleted {formatDate(lead.deletedDate)}</Badge>}
        {lead.reallocated && <Badge tone="amber">Reallocated</Badge>}
        {lead.routingOutcome !== 0 && <Badge tone="amber">{routingOutcome[lead.routingOutcome] ?? lead.routingOutcome}</Badge>}
        {lead.failedToSend && <Badge tone="red">Failed to send to Wilma Pro · {lead.sendAttempts} attempt(s)</Badge>}
        <a
          href={`/leads/${lead.id}`}
          target="_blank"
          rel="noreferrer"
          className="ml-auto inline-flex items-center gap-1 text-xs font-medium text-primary-600 hover:text-primary-700"
        >
          Open lead <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
        </a>
      </div>

      <dl className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <Field label="Sent to (current owner)">
          <div className="flex flex-wrap items-center gap-2">
            <Customer id={lead.customerId} />
            {lead.ownedBySav && <Badge tone="blue">SAV</Badge>}
          </div>
        </Field>
        <Field label={lead.isCollection ? "Collection price winner" : "Drop-off price winner"}>
          <Customer id={lead.isCollection ? lead.collectionCustomerId : lead.dropOffCustomerId} />
        </Field>
        <Field label="Price · commission">
          {formatPrice(lead.price)} · {formatPrice(lead.commission)}
        </Field>
        <Field label="Lead website">{leadWebsite[lead.website] ?? lead.website}</Field>
        {lead.failedToSend && (
          <Field label="Last send error">{lead.lastSendError ?? "Not recorded (failed before errors were saved)"}</Field>
        )}
        {lead.reallocated && <Field label="Reallocation (inferred)">{lead.reallocationNote}</Field>}
        {lead.routingReason && <Field label="Routing reason">{lead.routingReason}</Field>}
        {lead.duplicateOfLeadId && (
          <Field label="Duplicate of">
            <div className="flex flex-wrap items-center gap-2">
              <a href={`/leads/${lead.duplicateOfLeadId}`} target="_blank" rel="noreferrer" className="text-primary-600 hover:text-primary-700">
                Lead {shortId(lead.duplicateOfLeadId)}
              </a>
              <Customer id={lead.duplicateOfCustomerId} />
            </div>
          </Field>
        )}
        <Field label="Texts">
          {lead.messages.length === 0
            ? "None"
            : lead.messages.map((m, i) => (
                <div key={i} className="text-xs text-gray-600">
                  {formatDate(m.timestamp)} · {messageStatus[m.status] ?? m.status}
                  {m.recipient ? ` · ${m.recipient}` : ""}
                </div>
              ))}
        </Field>
      </dl>
    </div>
  );
}

function TraceResults({ vrm }: { vrm: string }) {
  const trace = useVrmTrace(vrm);

  if (trace.isLoading) return <LoadingWheel />;
  if (trace.isError) return <p className="text-sm text-red-600">Couldn't load the trace. Check the registration and try again.</p>;

  const data = trace.data?.data;
  if (!data || (data.quotes.length === 0 && data.leads.length === 0)) {
    return <p className="py-6 text-center text-sm text-gray-500">No quotes or leads found for {vrm}.</p>;
  }

  const quoteIds = new Set(data.quotes.map((q) => q.id));
  const orphanLeads = data.leads.filter((l) => !l.quoteId || !quoteIds.has(l.quoteId));

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-gray-500">
        {data.quotes.length} quote(s) · {data.leads.length} lead(s), oldest first. Names come from Wilma Core; reallocations
        aren't logged, so they're inferred from who owns the lead now.
      </p>

      {data.quotes.map((quote) => (
        <QuoteBlock key={quote.id} quote={quote} leads={data.leads.filter((l) => l.quoteId === quote.id)} />
      ))}

      {orphanLeads.length > 0 && (
        <div className="flex flex-col gap-3">
          <h4 className="text-sm font-semibold text-gray-900">Leads without a matching quote</h4>
          {orphanLeads.map((lead) => (
            <LeadBlock key={lead.id} lead={lead} />
          ))}
        </div>
      )}
    </div>
  );
}

export default function TraceVrmCard() {
  const [input, setInput] = useState("");
  const [vrm, setVrm] = useState<string | null>(null);
  const [open, setOpen] = useState(false);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const normalised = input.replace(/\s+/g, "").toUpperCase();
    if (!normalised) return;
    setVrm(normalised);
    setOpen(true);
  };

  return (
    <>
      <Modal open={open} setOpen={setOpen} width="sm:max-w-5xl">
        <div className="flex flex-col gap-4">
          <h3 className="text-base font-semibold text-gray-900">
            Trace for <span className="font-mono">{vrm}</span>
          </h3>
          <div className="max-h-[70vh] overflow-y-auto pr-1">{vrm && <TraceResults vrm={vrm} />}</div>
          <div className="flex justify-end">
            <button
              onClick={() => setOpen(false)}
              className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Close
            </button>
          </div>
        </div>
      </Modal>

      <form onSubmit={handleSubmit} className="flex flex-col gap-3">
        <p className="text-sm text-gray-600">
          Shows every quote and lead for a registration: the website it came from, who won the price, Superchat and email
          sends, who the lead went to, and whether it was reallocated.
        </p>
        <div className="flex gap-2">
          <input
            id="trace-vrm-input"
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="e.g. BG64 TKZ"
            className="min-w-0 flex-1 rounded-md border-gray-300 font-mono uppercase sm:text-sm"
          />
          <button
            type="submit"
            disabled={!input.trim()}
            className="inline-flex items-center justify-center gap-2 rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-60"
          >
            <MagnifyingGlassIcon className="h-4 w-4" />
            Trace VRM
          </button>
        </div>
      </form>
    </>
  );
}
